import { useRef, useLayoutEffect, useCallback } from 'react';
import { uniq } from 'ramda';
import { useSearchContext } from './useSearch';

const useSearchHighlight = () => {
  const ref = useRef();
  const { debouncedSearch: search } = useSearchContext();

  const highlight = useCallback(() => {
    if (!ref.current) return;
    if (!search) return;

    const textNodeInnerHTML = (textNode, innerHTML) => {
      const div = document.createElement('div');
      textNode.parentNode.insertBefore(div, textNode);
      div.insertAdjacentHTML('afterend', innerHTML);
      div.remove();
      textNode.remove();
    };

    const addHighlights = (textToChange) => {
      const escapedSearchStr = search.replace(/[.*+?^${}()|[\]\\]/g, '\\{body}');
      const matches = uniq(textToChange.match(new RegExp(escapedSearchStr, 'ig')) || []);
      const nextValue = matches.reduce((acc, match) => {
        const escapedMatch = match.replace(/[.*+?^${}()|[\]\\]/g, '\\{body}');
        return acc.replace(new RegExp(escapedMatch, 'g'), `<!--${match}-->`);
      }, textToChange);
      return nextValue
        .replace(/<!--/g, '<span class="highlight"><span class="highlighted-content">')
        .replace(/-->/g, '</span></span>');
    };

    const highlightSearchStr = (el) => {
      if (!el) {
        return;
      }
      [...el.children].forEach(highlightSearchStr);

      if (el.children.length) {
        [...el.childNodes].forEach((node) => {
          if (node.nodeType === 3) {
            const newContent = addHighlights(node.nodeValue);
            textNodeInnerHTML(node, newContent);
          }
        });
      } else {
        el.innerHTML = addHighlights(el.innerHTML);
      }
    };

    return highlightSearchStr(ref.current);
  }, [search]);

  useLayoutEffect(() => {
    highlight();
  }, [highlight]);

  return [ref, highlight];
};

export default useSearchHighlight;
